<template>
  <Modal
    :modal-active="modelValue"
    has-header-border
    has-footer-border
    max-width="max-w-[560px]"
    @close="close"
  >
    <template #header>
      <h2 class="text-xl font-bold text-textPrimary text-left">Enviar nova folha</h2>
    </template>

    <div class="flex flex-col gap-4 text-left">
      <p class="text-sm text-neutral50">
        A folha atual foi recusada e continuará disponível em “Folhas anteriores”. Ao enviar, o
        registro volta para <b>Aguardando Auditoria</b> com um novo prazo, e as linhas já
        transcritas voltam a ficar pendentes de conciliação.
      </p>
      <StepFile v-model="file" />
      <v-textarea
        v-model="justification"
        label="Justificativa"
        placeholder="Ex.: folha corrigida enviada pelo profissional via Suporte"
        variant="outlined"
        density="compact"
        rows="3"
        counter="1000"
        maxlength="1000"
        :hint="justificationHint"
        persistent-hint
        data-testid="new-sheet-justification"
      />
    </div>

    <template #footer>
      <div class="flex flex-col sm:flex-row sm:justify-end gap-3">
        <OutlineButton
          text="Cancelar"
          width="96px"
          height="40px"
          data-testid="new-sheet-cancel-button"
          @click="close"
        />
        <FilledButton
          text="Enviar nova folha"
          width="180px"
          height="40px"
          :disabled="!canSend || saving"
          data-testid="new-sheet-confirm-button"
          @click="confirm"
        />
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import Modal from '@/components/designSystem/modals/Modal.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import StepFile from '@/components/designSystem/modals/AttendanceUploadModal/StepFile.vue'

/**
 * rejected-sheet-resubmission (G1, CA4): a Auditoria anexa a folha corrigida ao
 * registro reprovado. O mínimo da justificativa espelha o DTO do backend (BE-3).
 */
const MIN_JUSTIFICATION = 10

const props = defineProps<{ modelValue: boolean; saving: boolean }>()
const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  confirm: [payload: { file: File; justification: string }]
}>()

const file = ref<File | null>(null)
const justification = ref('')

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      file.value = null
      justification.value = ''
    }
  }
)

const trimmed = computed(() => justification.value.trim())
const canSend = computed(() => !!file.value && trimmed.value.length >= MIN_JUSTIFICATION)
const justificationHint = computed(() =>
  trimmed.value.length < MIN_JUSTIFICATION
    ? `Obrigatória — mínimo de ${MIN_JUSTIFICATION} caracteres`
    : ''
)

function close() {
  emit('update:modelValue', false)
}

function confirm() {
  if (!canSend.value || props.saving || !file.value) return
  emit('confirm', { file: file.value, justification: trimmed.value })
}
</script>
